import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import SEO from '../components/SEO';
import FAQ from '../components/ui/FAQ';
import { homepageFAQs } from '../data/faqs';
import { sendRequirementsFormEmail } from '../utils/emailService';
import { scrollToElement } from '../utils/scrollUtils';
import { useContactInfo } from '../utils/contactObfuscation';

const LandingPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    requirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const contactInfo = useContactInfo();

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // SEO Data for Landing Page
  const seoData = {
    title: 'Get Your Custom Web & Mobile App Built | Professional Development Services | OctalChip',
    description: 'Transform your business with custom web and mobile applications. Get a free consultation and quote from our expert development team. Start your project today!',
    keywords: 'web development, mobile app development, custom software, development services, app development, website development, software development, development agency, custom applications, web applications, mobile applications, development quote, free consultation, development services, React, Node.js, Python, JavaScript, TypeScript, Flutter, React Native, iOS development, Android development, full-stack development, frontend development, backend development, database design, API development, cloud services, DevOps, UI/UX design, enterprise software, SaaS development, e-commerce development',
    canonical: 'https://www.octalchip.com/landingpage',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Custom Web & Mobile App Development Landing Page",
      "description": "Get your custom web and mobile applications built by expert developers. Free consultation and quote available.",
      "url": "https://www.octalchip.com/landingpage",
      "mainEntity": {
        "@type": "Organization",
        "name": "OctalChip",
        "url": "https://www.octalchip.com",
        "logo": "https://www.octalchip.com/favicon.svg",
        "description": "Professional web and mobile app development services",
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "email": "business@octalchip.com"
        }
      }
    },
    faqData: homepageFAQs
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const result = await sendRequirementsFormEmail(formData);
      
      if (result.success) {
        setSubmitMessage('Thank you! Your requirements have been submitted successfully. We\'ll get back to you within 24 hours with a detailed quote.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: '',
          requirements: ''
        });
      } else {
        setSubmitMessage('Sorry, there was an error submitting your requirements. Please try again or contact us directly.');
      }
    } catch (error) {
      setSubmitMessage('Sorry, there was an error submitting your requirements. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: 'Zap',
      title: 'Fast Delivery',
      description: 'Get your project delivered in 2-4 weeks with our agile development process'
    },
    {
      icon: 'Shield',
      title: 'Quality Guaranteed',
      description: '100% satisfaction guarantee with unlimited revisions until you\'re happy'
    },
    {
      icon: 'DollarSign',
      title: 'Competitive Pricing',
      description: 'Get professional development at 60% less than US agencies'
    },
    {
      icon: 'Users',
      title: 'Expert Team',
      description: 'Work with experienced developers who have 3-5+ years of expertise'
    }
  ];

  const services = [
    {
      title: 'Web Development',
      description: 'Custom websites, web applications, and e-commerce solutions',
      icon: 'Globe',
      features: ['React.js', 'Node.js', 'Python', 'Database Design']
    },
    {
      title: 'Mobile App Development',
      description: 'iOS, Android, and cross-platform mobile applications',
      icon: 'Smartphone',
      features: ['React Native', 'Flutter', 'Native iOS/Android', 'App Store Publishing']
    },
    {
      title: 'Backend Development',
      description: 'Robust server-side applications and API development',
      icon: 'Server',
      features: ['Node.js', 'Python/Django', 'Java/Spring', 'API Development']
    },
    {
      title: 'Cloud Development',
      description: 'Scalable cloud infrastructure and DevOps solutions',
      icon: 'Cloud',
      features: ['AWS/Azure/GCP', 'Docker/Kubernetes', 'CI/CD Pipelines', 'Serverless']
    },
    {
      title: 'UI/UX Design',
      description: 'User-centered design for exceptional digital experiences',
      icon: 'Palette',
      features: ['User Research', 'Wireframing', 'Prototyping', 'Design Systems']
    },
    {
      title: 'AI & Automation',
      description: 'AI-powered solutions and business process automation',
      icon: 'Cpu',
      features: ['Machine Learning', 'Chatbots', 'Data Analytics', 'Process Automation']
    },
    {
      title: 'Workflow Automation',
      description: 'n8n, Zapier, and Make automation services',
      icon: 'Settings',
      features: ['n8n Automation', 'Zapier Integration', 'Make Automation', 'API Integration']
    }
  ];

  const stats = [
    { number: '25+', label: 'Expert Developers', icon: 'Users' },
    { number: '100+', label: 'Projects Delivered', icon: 'CheckCircle' },
    { number: '98%', label: 'Client Satisfaction', icon: 'Star' },
    { number: '24/7', label: 'Support Available', icon: 'Headphones' }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Discovery & Planning',
      description: 'We understand your business goals, target audience, and technical requirements through detailed consultation.',
      icon: 'Search'
    },
    {
      step: '02',
      title: 'Design & Prototyping',
      description: 'Our designers create wireframes and prototypes to visualize your solution before development begins.',
      icon: 'Palette'
    },
    {
      step: '03',
      title: 'Development & Testing',
      description: 'Expert developers build your solution using agile methodology with continuous testing and quality assurance.',
      icon: 'Code'
    },
    {
      step: '04',
      title: 'Deployment & Support',
      description: 'We deploy your solution and provide ongoing maintenance, updates, and support to ensure optimal performance.',
      icon: 'Rocket'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      company: 'TechStart Inc.',
      role: 'CEO',
      text: 'OctalChip delivered our e-commerce platform in just 3 weeks. The quality exceeded our expectations and the team was incredibly responsive. Our sales increased by 300% in the first quarter!',
      rating: 5
    },
    {
      name: 'Mike Chen',
      company: 'InnovateCorp',
      role: 'CTO',
      text: 'We saved over $50,000 working with OctalChip compared to local agencies. The development quality was outstanding, and they delivered ahead of schedule. Highly recommended!',
      rating: 5
    },
    {
      name: 'Emily Rodriguez',
      company: 'GrowthLabs',
      role: 'Product Manager',
      text: 'The team at OctalChip understood our vision perfectly and delivered a mobile app that our users love. The app has 4.8 stars on both app stores with over 50K downloads.',
      rating: 5
    },
    {
      name: 'David Thompson',
      company: 'CloudScale Inc',
      role: 'VP Engineering',
      text: 'OctalChip\'s cloud infrastructure solutions helped us achieve 99.9% uptime. Their DevOps expertise and proactive support made all the difference.',
      rating: 5
    }
  ];

  const trustIndicators = [
    { text: 'ISO Certified', icon: 'Shield' },
    { text: 'GDPR Compliant', icon: 'Lock' },
    { text: 'NDA Protected', icon: 'FileText' },
    { text: 'Money-Back Guarantee', icon: 'CheckCircle' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        ogImage={seoData.ogImage}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Hero Section - Conversion Focused */}
      <section id="hero" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-[#f4faf8] via-white to-[#e8f5e8]">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 lg:gap-12 items-center">
              <div className="text-center lg:text-left">
                <div className="inline-block bg-[#00d690]/10 text-[#00d690] px-4 py-2 rounded-full text-sm font-medium mb-6">
                  Professional Development Services
                </div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 leading-tight text-gray-800 px-4">
                  World's Best Development Services - Get Your Custom Web & Mobile App Built Globally
                </h1>
                <p className="text-xl md:text-2xl mb-8 text-gray-600">
                  Trusted by businesses worldwide, we transform your business with professional web and mobile applications. 
                  Get a free consultation and detailed quote in 24 hours - recognized globally for excellence.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                  <Button
                    variant="primary"
                    animation="bounce"
                    className="px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base md:text-lg bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                    onClick={async () => {
                      await scrollToElement('#get-quote', { 
                        smooth: true,
                        maxRetries: 15,
                        retryDelay: 150
                      }).catch(() => {
                        // Silent fail - scrollToElement handles retries internally
                      });
                    }}
                  >
                    Get Free Quote Now
                  </Button>
                  <Button
                    variant="outline"
                    animation="pulse"
                    className="px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base md:text-lg border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white"
                    onClick={() => {
                      window.open('https://calendly.com/business-octalchip/30min', '_blank');
                    }}
                  >
                    Schedule Free Call
                  </Button>
                </div>

                <div className="flex flex-col sm:flex-row gap-6 text-sm">
                  <div className="flex items-center justify-center lg:justify-start">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">Free Consultation</span>
                  </div>
                  <div className="flex items-center justify-center lg:justify-start">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">24-Hour Response</span>
                  </div>
                  <div className="flex items-center justify-center lg:justify-start">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">No Hidden Costs</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="MessageSquare" size={32} className="text-[#00d690]" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-800">Get Your Free Quote</h3>
                  <p className="text-gray-600 mb-6">Tell us about your project and get a detailed estimate in 24 hours</p>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input 
                      type="text" 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Name"
                      required
                    />
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Email"
                      required
                    />
                    <input 
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Phone"
                      required
                    />
                    <select 
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      required
                    >
                      <option value="">Select Project Type</option>
                      <option value="Web Development">Web Development</option>
                      <option value="Mobile App Development">Mobile App Development</option>
                      <option value="E-commerce">E-commerce</option>
                      <option value="AI/ML Solution">AI/ML Solution</option>
                      <option value="Workflow Automation">Workflow Automation</option>
                      <option value="Other">Other</option>
                    </select>
                    <textarea 
                      name="requirements"
                      value={formData.requirements}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Brief description of your project..."
                      required
                    ></textarea>
                    
                    {submitMessage && (
                      <div className={`p-4 rounded-lg text-center ${
                        submitMessage.includes('Thank you') 
                          ? 'bg-green-100 text-green-700 border border-green-200' 
                          : 'bg-red-100 text-red-700 border border-red-200'
                      }`}>
                        {submitMessage}
                      </div>
                    )}
                    
                    <Button 
                      type="submit"
                      variant="primary"
                      animation="glow"
                      className="w-full py-3 bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Get Free Quote'}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-[#00d690]/5 via-white to-[#00d690]/10">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={stat.icon} size={32} className="text-[#00d690]" />
                </div>
                <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-2">{stat.number}</div>
                <div className="text-sm sm:text-base text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 px-4">Why Choose OctalChip?</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Get professional development services with guaranteed quality and fast delivery
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-4 sm:p-6 bg-gray-50 rounded-xl hover:shadow-lg transition-shadow duration-300">
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <Icon name={benefit.icon} size={24} className="sm:w-7 sm:h-7 md:w-8 md:h-8 text-[#00d690]" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">{benefit.title}</h3>
                <p className="text-sm sm:text-base text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section id="pain-points" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 px-4">Common Development Challenges We Solve</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              We understand the pain points businesses face. Here's how we help solve them
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">High Development Costs</h3>
                  <p className="text-gray-600">Agency rates are expensive making projects unaffordable. We provide expert development at 60% lower costs with same quality, making professional solutions accessible.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Long Development Timelines</h3>
                  <p className="text-gray-600">Projects take months delaying business goals. We use agile methodology and expert teams delivering projects in 2-4 weeks without compromising quality.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Poor Code Quality</h3>
                  <p className="text-gray-600">Low-quality code leads to bugs and maintenance issues. We write clean, maintainable code following best practices ensuring long-term reliability and scalability.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Lack of Technical Expertise</h3>
                  <p className="text-gray-600">Finding skilled developers is difficult and time-consuming. We provide expert teams with 3-5+ years experience across all modern technologies and frameworks.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Communication & Transparency Issues</h3>
                  <p className="text-gray-600">Poor communication leads to misunderstandings and delays. We maintain transparent communication with regular updates, progress reports, and direct access to your team.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Scalability & Maintenance Concerns</h3>
                  <p className="text-gray-600">Solutions that don't scale or are hard to maintain become liabilities. We build scalable architectures with proper documentation and ongoing support ensuring long-term success.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 px-4">Our Development Process</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              A proven methodology that ensures quality, transparency, and on-time delivery
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 h-full">
                  <div className="mb-4">
                    <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg">
                      {step.step}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{step.title}</h3>
                  <p className="text-gray-600 text-sm sm:text-base">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 px-4">Our Development Services</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Comprehensive development solutions to bring your ideas to life
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {services.map((service, index) => {
              const serviceRoutes = {
                'Web Development': '/services/web-development',
                'Mobile App Development': '/services/mobile-app-development',
                'Backend Development': '/services/backend-development',
                'Cloud Development': '/services/cloud-devops',
                'UI/UX Design': '/services/ui-ux-design',
                'AI & Automation': '/services/ai-integration',
                'Workflow Automation': '/services/workflow-automation'
              };
              
              return (
                <div 
                  key={index} 
                  className="bg-white p-4 sm:p-6 md:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group"
                  onClick={() => serviceRoutes[service.title] && navigate(serviceRoutes[service.title])}
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-[#00d690]/20 transition-colors">
                    <Icon name={service.icon} size={24} className="sm:w-7 sm:h-7 md:w-8 md:h-8 text-[#00d690]" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 group-hover:text-[#00d690] transition-colors">{service.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">{service.description}</p>
                  <ul className="space-y-2 mb-4">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                        <Icon name="Check" size={16} className="text-[#00d690] mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {serviceRoutes[service.title] && (
                    <div className="flex items-center text-[#00d690] font-medium text-sm group-hover:gap-2 transition-all">
                      <span>Learn More</span>
                      <Icon name="ArrowRight" size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          <div className="text-center mt-12">
            <Button
              variant="outline"
              animation="scale"
              className="border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white px-8 py-3"
              onClick={() => navigate('/services')}
            >
              View All Services
            </Button>
          </div>
        </div>
      </section>

      {/* Marketing Strategies Section */}
      <section id="offers" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 px-4">Special Offers & Free Trials</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Start your project with confidence - try our services risk-free
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {/* Free Trial Card */}
            <div className="bg-white text-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">
                  POPULAR
                </span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Play" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Free Trial</h3>
              <p className="text-gray-600 mb-6">
                Get 2 hours of free development work to experience our quality and expertise
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>2 hours of free development</span>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>No commitment required</span>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>Keep the code you receive</span>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>Full project consultation</span>
                </li>
              </ul>
              <Button
                variant="primary"
                animation="shake"
                className="w-full bg-[#00d690] text-white hover:bg-[#00b876]"
                onClick={async () => {
                  await scrollToElement('#get-quote', { 
                    smooth: true,
                    maxRetries: 15,
                    retryDelay: 150
                  }).catch(() => {
                    // Silent fail - scrollToElement handles retries internally
                  });
                }}
              >
                Start Free Trial
              </Button>
            </div>

            {/* Free Credits Card */}
            <div className="bg-white text-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">
                  NEW
                </span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Gift" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Free Credits</h3>
              <p className="text-gray-600 mb-6">
                Get $500 in development credits when you sign up for your first project
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>$500 free credits</span>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>Valid for 30 days</span>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>Deducted from final invoice</span>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>Use on any project</span>
                </li>
              </ul>
              <Button
                variant="primary"
                animation="float"
                className="w-full bg-[#00d690] text-white hover:bg-[#00b876]"
                onClick={async () => {
                  await scrollToElement('#get-quote', { 
                    smooth: true,
                    maxRetries: 15,
                    retryDelay: 150
                  }).catch(() => {
                    // Silent fail - scrollToElement handles retries internally
                  });
                }}
              >
                Claim Free Credits
              </Button>
            </div>

            {/* Money Back Guarantee Card */}
            <div className="bg-white text-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">
                  GUARANTEE
                </span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Shield" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">100% Satisfaction</h3>
              <p className="text-gray-600 mb-6">
                Not satisfied? Get your money back within 7 days of project completion
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>7-day money-back guarantee</span>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>No questions asked</span>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>Keep all deliverables</span>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>Full refund guarantee</span>
                </li>
              </ul>
              <Button
                variant="primary"
                animation="wiggle"
                className="w-full bg-[#00d690] text-white hover:bg-[#00b876]"
                onClick={async () => {
                  await scrollToElement('#get-quote', { 
                    smooth: true,
                    maxRetries: 15,
                    retryDelay: 150
                  }).catch(() => {
                    // Silent fail - scrollToElement handles retries internally
                  });
                }}
              >
                Start Risk-Free
              </Button>
            </div>
          </div>

          {/* Additional Offers */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Early Bird Discount */}
            <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mr-4">
                  <Icon name="Clock" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Early Bird Discount</h3>
                  <p className="text-[#00d690] font-medium">Save 20% on first project</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                Book your project within 48 hours of consultation and get 20% off your total development cost.
              </p>
              <Button
                variant="outline"
                animation="rotate"
                className="border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white"
                onClick={() => {
                  window.open('https://calendly.com/business-octalchip/30min', '_blank');
                }}
              >
                Book Consultation Now
              </Button>
            </div>

            {/* Referral Program */}
            <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mr-4">
                  <Icon name="Users" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Referral Program</h3>
                  <p className="text-[#00d690] font-medium">Earn $200 per referral</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                Refer a friend or business and both of you get $200 in development credits when they complete their first project.
              </p>
              <Button
                variant="outline"
                animation="slide"
                className="border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white"
                onClick={async () => {
                  await scrollToElement('#get-quote', { 
                    smooth: true,
                    maxRetries: 15,
                    retryDelay: 150
                  }).catch(() => {
                    // Silent fail - scrollToElement handles retries internally
                  });
                }}
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators Section */}
      <section id="trust" className="py-8 sm:py-12 bg-white border-t border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 sm:gap-12">
            {trustIndicators.map((indicator, index) => (
              <div key={index} className="flex items-center gap-3">
                <Icon name={indicator.icon} size={24} className="text-[#00d690]" />
                <span className="text-sm sm:text-base font-medium text-gray-700">{indicator.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 px-4">What Our Clients Say</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Don't just take our word for it - hear from our satisfied clients
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className={`bg-white p-6 sm:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 ${
                  index === currentTestimonial ? 'ring-2 ring-[#00d690]' : ''
                }`}
              >
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 bg-[#00d690]/10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <Icon name="User" size={24} className="text-[#00d690]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Icon key={i} name="Star" size={16} className="text-yellow-400 mr-1" />
                      ))}
                    </div>
                    <p className="text-gray-600 mb-4 italic leading-relaxed">"{testimonial.text}"</p>
                    <div>
                      <p className="font-semibold text-gray-800">{testimonial.name}</p>
                      <p className="text-sm text-gray-500">{testimonial.role}, {testimonial.company}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Testimonial Navigation Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 btn-animate-pulse ${
                  index === currentTestimonial ? 'bg-[#00d690] w-8' : 'bg-gray-300'
                }`}
                aria-label={`View testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 px-4">Our Development Portfolio</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              See how we've helped businesses worldwide build successful digital solutions
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow border border-gray-200">
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="Globe" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">E-commerce Platform</h3>
              <p className="text-gray-600 mb-4">Built comprehensive e-commerce platform with React and Node.js featuring real-time inventory, secure payments, and admin dashboard. Increased sales by 300% and improved user experience significantly.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">React</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Node.js</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">MongoDB</span>
              </div>
              <p className="text-sm text-gray-500">Client: RetailTech Solutions | Location: USA</p>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow border border-gray-200">
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="Smartphone" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Mobile Banking App</h3>
              <p className="text-gray-600 mb-4">Developed secure mobile banking app with React Native featuring biometric authentication, real-time transactions, and investment tracking. Achieved 4.9★ rating with 100K+ downloads.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">React Native</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Security</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">FinTech</span>
              </div>
              <p className="text-sm text-gray-500">Client: FinTech Innovations | Location: UK</p>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow border border-gray-200">
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="Brain" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">AI-Powered Analytics Platform</h3>
              <p className="text-gray-600 mb-4">Created ML-powered analytics platform with Python and TensorFlow providing predictive insights. Improved decision-making accuracy by 85% and reduced operational costs by 40%.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Python</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">TensorFlow</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">ML</span>
              </div>
              <p className="text-sm text-gray-500">Client: DataDriven Inc | Location: Canada</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="get-quote" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-[#f4faf8] via-white to-[#e8f5e8]">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-block bg-[#00d690]/10 text-[#00d690] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6">
              Start Your Project Today
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-gray-800 px-4">Ready to Start Your Project?</h2>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-6 sm:mb-8 text-gray-600 px-4">
              Get a free consultation and detailed quote for your project. 
              Our team will help you bring your vision to life.
            </p>
            
            <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 max-w-2xl mx-auto shadow-xl border border-gray-100">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                    placeholder="Your Name"
                    required
                  />
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                    placeholder="Your Email"
                    required
                  />
                </div>
                <input 
                  type="tel" 
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                  placeholder="Your Phone"
                  required
                />
                <select 
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                  required
                >
                  <option value="">Select Project Type</option>
                  <option value="Web Development">Web Development</option>
                  <option value="Mobile App Development">Mobile App Development</option>
                  <option value="E-commerce">E-commerce</option>
                  <option value="AI/ML Solution">AI/ML Solution</option>
                  <option value="Other">Other</option>
                </select>
                <textarea 
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                  placeholder="Describe your project requirements, goals, and timeline..."
                  required
                ></textarea>
                
                {submitMessage && (
                  <div className={`p-4 rounded-lg text-center ${
                    submitMessage.includes('Thank you') 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-red-100 text-red-700 border border-red-200'
                  }`}>
                    {submitMessage}
                  </div>
                )}
                
                <Button 
                  type="submit"
                  variant="primary"
                  animation="pulse"
                  className="w-full py-3 bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Get Free Quote & Consultation'}
                </Button>
              </form>
            </div>
            
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <div className="flex items-center justify-center">
                <Icon name="Phone" size={20} className="text-[#00d690] mr-2" />
                {contactInfo.phoneLink && (
                  <a href={contactInfo.phoneLink} className="text-gray-700 hover:text-[#00d690]">
                    {contactInfo.phone}
                  </a>
                )}
              </div>
              <div className="flex items-center justify-center">
                <Icon name="Mail" size={20} className="text-[#00d690] mr-2" />
                <a href="mailto:business@octalchip.com" className="text-gray-700 hover:text-[#00d690]">
                  business@octalchip.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Reviews Section */}
      <section id="trust-reviews" className="py-8 md:py-12 bg-gradient-to-br from-[#00d690]/5 to-[#00d690]/10">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 mb-6 md:mb-8">
              Trusted by Leading Companies Worldwide
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {/* Clutch.co Column */}
              <div className="bg-white rounded-xl p-4 md:p-6 shadow-md">
                <div className="flex flex-col items-center gap-3">
                  <div className="text-2xl md:text-3xl font-bold text-gray-800">
                    Clutch
                    <span className="inline-block w-2 h-2 bg-red-500 rounded-full ml-1"></span>
                  </div>
                  <div className="flex items-center justify-center gap-1 mb-2">
                    <span className="text-xl md:text-2xl font-bold text-gray-800">4.9</span>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Icon
                          key={i}
                          name="Star"
                          size={16}
                          className={`mx-0.5 ${i < 4 ? 'text-[#00d690] fill-current' : i === 4 ? 'text-[#00d690] fill-current opacity-90' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                  </div>
                  <a
                    href="https://clutch.co/profile/octalchip#reviews"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#00d690] hover:underline font-medium"
                  >
                    View Profile
                  </a>
                </div>
              </div>

              {/* Trustpilot Column */}
              <div className="bg-white rounded-xl p-4 md:p-6 shadow-md">
                <div className="flex flex-col items-center gap-3">
                  <div className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-1">
                    <Icon name="Star" size={24} className="text-[#00d690] fill-current" />
                    Trustpilot
                  </div>
                  <div className="flex items-center justify-center gap-1 mb-2">
                    <span className="text-xl md:text-2xl font-bold text-gray-800">4.7</span>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Icon
                          key={i}
                          name="Star"
                          size={16}
                          className={`mx-0.5 ${i < 4 ? 'text-[#00d690] fill-current' : i === 4 ? 'text-[#00d690] fill-current opacity-90' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                  </div>
                  <a
                    href="https://www.trustpilot.com/review/octalchip.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#00d690] hover:underline font-medium"
                  >
                    View Reviews
                  </a>
                </div>
              </div>

              {/* G2 Column */}
              <div className="bg-white rounded-xl p-4 md:p-6 shadow-md">
                <div className="flex flex-col items-center gap-3">
                  <div className="text-2xl md:text-3xl font-bold text-gray-800">
                    G2
                  </div>
                  <div className="flex items-center justify-center gap-1 mb-2">
                    <span className="text-xl md:text-2xl font-bold text-gray-800">4.5</span>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Icon
                          key={i}
                          name="Star"
                          size={16}
                          className={`mx-0.5 ${i < 4 ? 'text-[#00d690] fill-current' : i === 4 ? 'text-[#00d690] fill-current opacity-90' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                  </div>
                  <a
                    href="https://www.g2.com/products/octalchip/reviews"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#00d690] hover:underline font-medium"
                  >
                    View Reviews
                  </a>
                </div>
              </div>

              {/* GoodFirms Column */}
              <div className="bg-white rounded-xl p-4 md:p-6 shadow-md">
                <div className="flex flex-col items-center gap-3">
                  <div className="text-2xl md:text-3xl font-bold text-gray-800">
                    GoodFirms
                  </div>
                  <div className="flex items-center justify-center gap-1 mb-2">
                    <span className="text-xl md:text-2xl font-bold text-gray-800">4.8</span>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Icon
                          key={i}
                          name="Star"
                          size={16}
                          className={`mx-0.5 ${i < 4 ? 'text-[#00d690] fill-current' : i === 4 ? 'text-[#00d690] fill-current opacity-90' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                  </div>
                  <a
                    href="https://www.goodfirms.co/company/octalchip"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#00d690] hover:underline font-medium"
                  >
                    View Profile
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={homepageFAQs}
        title="Frequently Asked Questions"
        description="Common questions about our development services, process, and working with OctalChip."
      />

      <Footer />
    </div>
  );
};

export default LandingPage; 